import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

export default function CustomerList({ onSelectCustomer }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  async function fetchCustomers() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      try {
        // Fetch profiles joined with order counts
        // Every customer has a profile since account creation is required
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });

        if (profileError) throw profileError;

        // Fetch order counts grouped by customer_id
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('customer_id');

        if (orderError) throw orderError;

        // Count orders per customer_id
        const orderCountMap = {};
        orderData?.forEach(order => {
          if (order.customer_id) {
            orderCountMap[order.customer_id] = (orderCountMap[order.customer_id] || 0) + 1;
          }
        });

        // Merge profiles with order counts, skip admin accounts
        const customerList = profileData
          .filter(profile => !profile.is_admin)
          .map(profile => ({
            ...profile,
            order_count: orderCountMap[profile.id] || 0
          }));

        setCustomers(customerList);
      } catch (err) {
        console.error("Error fetching customers:", err);
      }
    }
    setLoading(false);
  }

  const filteredCustomers = customers.filter(customer =>
    customer.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search customers by name or email..."
            className="input-field !pl-12 !rounded-full bg-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SafeIcon name="Search" className="absolute left-5 top-1/2 -translate-y-1/2 text-sage-300 text-xl" />
        </div>
        <div className="bg-white px-5 py-3 rounded-full border border-sage-100 shadow-sm flex items-center gap-2 text-sm text-sage-500 font-semibold whitespace-nowrap">
          <SafeIcon name="Users" className="text-blush-300" />
          {filteredCustomers.length} Customer{filteredCustomers.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-soft border border-sage-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-sage-50 border-b border-sage-100 text-[10px] text-sage-600 uppercase tracking-widest font-bold">
                <th className="p-6">Customer</th>
                <th className="p-6">Contact</th>
                <th className="p-6">Orders</th>
                <th className="p-6">Member Since</th>
                <th className="p-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sage-100">
              {filteredCustomers.map(customer => (
                <tr
                  key={customer.id}
                  onClick={() => onSelectCustomer(customer)}
                  className="hover:bg-warmwhite transition-colors cursor-pointer group"
                >
                  <td className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-blush-100 flex items-center justify-center text-blush-500 font-bold text-lg group-hover:bg-blush-200 transition-colors">
                        {customer.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <span className="font-bold text-charcoal group-hover:text-blush-500 transition-colors">
                        {customer.full_name || 'No name'}
                      </span>
                    </div>
                  </td>
                  <td className="p-6">
                    <span className="text-sm text-charcoal block font-medium">{customer.email}</span>
                    <span className="text-xs text-sage-400 font-semibold">{customer.phone || 'No phone'}</span>
                  </td>
                  <td className="p-6">
                    <div className="flex items-center gap-2 text-sage-600 font-bold text-sm">
                      <SafeIcon name="ShoppingBag" className="text-blush-300" />
                      {customer.order_count} {customer.order_count === 1 ? 'Order' : 'Orders'}
                    </div>
                  </td>
                  <td className="p-6">
                    <span className="text-sm text-sage-500 font-medium">
                      {new Date(customer.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="p-6 text-right">
                    <button className="btn-secondary !py-2 !px-4 !text-[10px]">
                      View Profile
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div className="p-12 text-center text-sage-400 font-bold uppercase tracking-widest text-xs">Loading customers...</div>}
        {!loading && filteredCustomers.length === 0 && <div className="p-12 text-center text-sage-400">No customers found.</div>}
      </div>
    </div>
  );
}
